import { Button, confirm, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { HelpCircle, PlusIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { LeaveRequestStatus } from "@/modules/hr/domain/contracts";
import { useDeleteLeaveRequest, useLeaveRequestsQuery } from "../api/queries";
import { HrTour } from "../tour/HrTour";
import { leaveRequestListTourSteps } from "../tour/hrTourSteps";
import { useHrTour } from "../tour/useHrTour";
import { LeaveRequestTable } from "../ui/LeaveRequestTable";

type LeaveSortBy = "leaveTypeName" | "startDate" | "days" | "status";

export function LeaveRequestListPage() {
  const navigate = useNavigate({ from: "/app/hr/leave/requests" });
  const search = useSearch({ from: "/app/hr/leave/requests" });
  const statusFilter = search.status;
  const offset = search.offset;
  const limit = search.limit;
  const sortBy = search.sortBy as LeaveSortBy | undefined;
  const sortOrder = search.sortOrder as "asc" | "desc" | undefined;

  const tour = useHrTour("hr-leave-list-tour-completed");
  const deleteLeave = useDeleteLeaveRequest();

  const query = useLeaveRequestsQuery({
    limit,
    offset,
    status: statusFilter as LeaveRequestStatus | undefined,
    sortBy,
    sortOrder,
  });

  const data = query.data?.data ?? [];
  const totalCount = query.data?.meta?.total ?? 0;

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight">ຄຳຂໍລາ</h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={tour.startTour}
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                title="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
            <p className="mt-1 text-muted-foreground">
              ຕິດຕາມປະຫວັດ ແລະ ສະຖານະຄຳຂໍລາຂອງທ່ານ
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              data-tourid="hr-leave-list-create-button"
              onClick={() => navigate({ to: "/app/hr/leave/requests/new" })}
              className="bg-black text-white hover:bg-black/90"
            >
              <PlusIcon className="size-4" />
              ສ້າງຄຳຂໍລາໃໝ່
            </Button>
          </div>
        </div>

        <div data-tourid="hr-leave-requests-table">
          <LeaveRequestTable
            data={data}
            isLoading={query.isLoading}
            totalCount={totalCount}
            offset={offset}
            limit={limit}
            onPageChange={(o, l) =>
              navigate({ search: { ...search, offset: o, limit: l } })
            }
            statusFilter={statusFilter}
            onStatusFilterChange={(s) =>
              navigate({ search: { ...search, status: s, offset: 0 } })
            }
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortingChange={(id, desc) =>
              navigate({
                search: {
                  ...search,
                  sortBy: id,
                  sortOrder: desc ? "desc" : "asc",
                  offset: 0,
                },
              })
            }
            onEdit={(id) =>
              navigate({
                to: "/app/hr/leave/requests/$id/edit",
                params: { id },
              })
            }
            onDelete={async (id) => {
              const ok = await confirm({
                title: "ລຶບຄຳຂໍລາ",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຄຳຂໍລານີ້?",
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok)
                toast.promise(deleteLeave.mutateAsync(id), {
                  loading: "ກຳລັງລຶບ...",
                  success: "ລຶບຄຳຂໍລາສຳເລັດ",
                  error: "ບໍ່ສາມາດລຶບຄຳຂໍລາໄດ້",
                });
            }}
          />
        </div>

        <HrTour
          steps={leaveRequestListTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
